import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import JsonLd from '@/components/base/JsonLd';

const sections = [
  {
    title: 'About SettleSpace',
    paragraphs: [
      'SettleSpace assists property owners and prospective tenants with rental transactions.',
      'Our services may include:',
      {
        type: 'bullets',
        items: [
          'Connecting owners with prospective tenants and tenants with suitable rental properties',
          'Coordinating property visits',
          'Communicating relevant information between the parties',
          'Coordinating rental agreement preparation and registration',
          'Coordinating police verification',
          'Coordinating society NOC procedures where applicable',
          'Coordinating biometric appointments where required',
          'Coordinating basic cleaning and pest control before move-in',
          'Assisting with payment coordination and transaction records',
          'Coordinating move-in and handover',
          'Inspecting the property at the end of the tenancy',
          'Providing reasonable assistance with matters arising from the rental process',
          'Providing priority assistance with finding an alternative tenant or property in the circumstances described in these Terms',
        ],
      },
      'SettleSpace currently focuses on rental transactions and does not guarantee that a suitable property or tenant will always be available.',
    ],
  },
  {
    title: 'Nature of SettleSpace\'s Services',
    paragraphs: [
      'SettleSpace acts as a rental service and coordination provider.',
      'We assist with the rental process but do not become a party to the rental agreement between the owner and tenant.',
      'The rental agreement, rent, security deposit, property condition, tenancy obligations, notice period, lock-in period and other terms governing the tenancy are matters between the owner and tenant and are governed by their rental agreement and applicable law.',
      'SettleSpace does not own, lease, manage or take possession of the properties it assists with unless expressly agreed otherwise.',
    ],
  },
  {
    title: 'Information Provided by Owners and Tenants',
    paragraphs: [
      'Owners and tenants must provide information and documents that are accurate, current and complete to the best of their knowledge.',
      'Owners may be required to provide documents such as:',
      {
        type: 'bullets',
        items: [
          'Aadhaar Card',
          'PAN Card',
          'Index II or other ownership-related documents',
          'Electricity bills',
          'Possession or parking letters',
          'Other documents reasonably required to verify ownership or property details',
        ],
      },
      'Tenants may be required to provide documents such as:',
      {
        type: 'bullets',
        items: [
          'Aadhaar Card',
          'PAN Card',
          'Work ID, where applicable',
          'Other documents reasonably required for the rental or verification process',
        ],
      },
      'SettleSpace may cross-check documents and information provided to us.',
      'If information or documentation cannot be satisfactorily verified, SettleSpace may require corrected or additional documents before proceeding.',
      'If the required information cannot be verified or a material discrepancy remains unresolved, SettleSpace may halt the process to avoid potential legal, financial or practical problems for the owner, tenant or SettleSpace.',
      'Providing false, misleading, altered or invalid information may result in the transaction being stopped and may have legal consequences for the person who provided it.',
    ],
  },
  {
    title: 'Payment and Refund Policy',
    paragraphs: [
      'All service fees, consultancy charges, and onboarding processing costs paid to SettleSpace are strictly non-refundable once service procurement begins or field assistance is rendered. In the event of an accidental duplicate transaction or a documented system billing error, the duplicate excess amount will be verified and issued back to the original payment source within 7-10 business days.',
    ],
  },
  {
    title: 'Property Ownership Verification',
    paragraphs: [
      'Ownership-related documents are generally obtained and checked before a property is placed for rent through SettleSpace.',
      'If the documents provided do not reasonably establish the owner\'s connection with the property, SettleSpace may refuse to place or continue the property in the rental process.',
      'SettleSpace relies on the documents and information available to it and on the verification reasonably possible through the information provided.',
      'SettleSpace does not guarantee that every undisclosed physical, legal, structural, society-related or other issue concerning a property can be discovered before tenancy.',
    ],
  },
  {
    title: 'Property Information',
    paragraphs: [
      'Owners are expected to provide accurate information about the property, including relevant features, fittings, conditions, restrictions and other matters that may reasonably affect the rental.',
      'SettleSpace may communicate the information provided by the owner to prospective tenants.',
      'Tenants are also expected to communicate their requirements accurately before proceeding.',
      'If a tenant makes new requirements after the rental terms have already been discussed or agreed and the owner is unable or unwilling to fulfil those new requirements, the booking amount does not automatically become refundable solely because of those newly introduced requirements. Any refund in such circumstances is subject to the owner\'s decision and the circumstances of the transaction.',
    ],
  },
  {
    title: 'Property Visits and Keys',
    paragraphs: [
      'Where SettleSpace receives keys from an owner for the purpose of showcasing a property, SettleSpace will take reasonable care of the keys and maintain appropriate records of the keys entrusted to it.',
      'A written or electronic acknowledgement may be provided when keys are received.',
      'Keys will not knowingly be duplicated, misused or used to access the property without a legitimate reason connected with the rental service and the owner\'s knowledge or permission.',
      'An owner may request the return of keys at any time. Where a property visit has already been scheduled, the keys may need to remain available until the scheduled visit is completed.',
      'After a property visit, SettleSpace will take reasonable steps to ensure that relevant doors and windows are secured and required switches are turned off.',
      'Prospective tenants should not use the property\'s restrooms during property visits unless otherwise permitted.',
    ],
  },
  {
    title: 'Rental Agreement and Registration',
    paragraphs: [
      'SettleSpace may coordinate the rental agreement process through a third-party agreement or registration service provider.',
      'The agreement process generally takes approximately 3 to 7 working days, but this is an estimate and not a guaranteed completion period.',
      'Agreement charges are generally shared equally between the owner and tenant unless otherwise agreed.',
      'The final cost may depend on factors including rent, security deposit and tenure.',
      'Minor corrections or changes may be possible after submission where the relevant registration process permits them.',
      'SettleSpace may coordinate the required changes with the relevant third-party provider.',
    ],
  },
  {
    title: 'Police Verification and Society Procedures',
    paragraphs: [
      'SettleSpace may coordinate police verification and society NOC procedures where applicable.',
      'Police verification is carried out by the relevant authority or service provider to verify the required personal information and other applicable details.',
      'If police verification identifies an issue concerning the owner or tenant, the concerned person may be required to resolve the issue directly before the rental process can proceed.',
      'SettleSpace may assist with coordination and communication but cannot guarantee the outcome of a police verification process or an authority\'s decision.',
      'Where a society NOC or other society procedure is required, SettleSpace will coordinate with the owner, tenant and relevant society representatives as necessary.',
      'If an application or documentation issue causes a problem, SettleSpace will assist the relevant parties in completing what is required.',
      'If the issue cannot be resolved, SettleSpace may halt the transaction to avoid future problems.',
    ],
  },
  {
    title: 'Third-Party Service Providers',
    paragraphs: [
      'Certain parts of the rental process may be performed by independent third parties, companies or individuals, including agreement registration, police verification, biometric services and other related processes.',
      'SettleSpace coordinates with such parties and passes on relevant information and requirements.',
      'Where appropriate, SettleSpace may connect the owner or tenant directly with the relevant third-party provider to improve clarity and communication.',
      'SettleSpace is not responsible for delays, failures or decisions caused solely by an independent third-party provider or government authority, although we will make reasonable efforts to assist in resolving or coordinating the issue.',
    ],
  },
  {
    title: 'Booking Amount',
    paragraphs: [
      'The booking amount, where applicable, is paid directly to the owner.',
      'SettleSpace does not collect, hold or retain the booking amount.',
      'The booking amount may be adjusted against the first month\'s rent or security deposit according to the arrangement between the owner and tenant.',
      'Where the owner is unable to provide the property or the necessities that were agreed upon as part of the rental arrangement, the owner may be required to refund the booking amount in accordance with the agreed terms and circumstances.',
      'If the tenant introduces new requirements after the arrangement has been discussed and the owner cannot fulfil those newly introduced requirements, the booking amount is not automatically refundable. The owner may decide whether to retain or refund it depending on the circumstances.',
      'SettleSpace may assist with communication and coordination regarding the booking amount but does not become responsible for the owner\'s obligation to refund or retain it.',
    ],
  },
  {
    title: 'Rent and Security Deposit',
    paragraphs: [
      'Rent and the security deposit are paid directly between the tenant and owner.',
      'SettleSpace does not hold the first month\'s rent, security deposit or booking amount on behalf of either party.',
      'Online bank transfer or UPI is generally preferred because it provides a clear transaction record. Cash payments may be made between the owner and tenant where mutually agreed by them.',
      'SettleSpace may assist with confirming payment details and keeping track of whether required payments have been completed.',
      'SettleSpace is not responsible for a payment incorrectly transferred by a party where accurate payment details were provided by SettleSpace.',
    ],
  },
  {
    title: 'SettleSpace Service Fee',
    paragraphs: [
      'The SettleSpace service fee is one month\'s rent for rental tenures of 1, 2 or 3 years, unless a different fee is expressly agreed in writing.',
      'The SettleSpace service fee becomes payable only after the relevant documentation process has been completed, including:',
      {
        type: 'bullets',
        items: [
          'The rental agreement having been submitted and received through the applicable process',
          'Police verification having been completed',
          'Society NOC having been completed, where applicable',
        ],
      },
      'The SettleSpace service fee must be cleared before the tenant\'s move-in or handover is facilitated.',
      'The fee is not automatically payable merely because an enquiry was made, a property was shown or initial discussions took place.',
      'If a customer asks SettleSpace to stop or discontinue the service before the transaction reaches the stage at which the service fee becomes payable, the SettleSpace service fee will not be payable.',
    ],
  },
  {
    title: 'Non-Payment of the SettleSpace Service Fee',
    paragraphs: [
      'Once the service fee has become payable, the relevant party is expected to clear it promptly.',
      'For a tenant, if the applicable SettleSpace service fee has not been cleared before move-in, SettleSpace may, in coordination with the owner and relevant parties, withhold completion of the move-in and handover process, including release of keys or documents being handled through SettleSpace for completion of the transaction, until the fee is cleared.',
      'For an owner, if the applicable SettleSpace service fee has not been cleared, SettleSpace may suspend the tenant\'s move-in coordination and may withhold completion or release of documents being handled through SettleSpace until the outstanding fee is cleared.',
      'SettleSpace may also discontinue its involvement in that rental transaction and assist the affected party in looking for an alternative tenant or property.',
      'SettleSpace may seek recovery of an unpaid service fee and reasonable, documented costs arising from a party\'s breach, subject to applicable law.',
    ],
  },
  {
    title: 'Cleaning and Pest Control',
    paragraphs: [
      'Basic cleaning and pest control are included as part of SettleSpace\'s service and are arranged and paid for by SettleSpace before the tenant moves into the property.',
      'SettleSpace will make reasonable efforts to ensure that the property is prepared before handover.',
    ],
  },
  {
    title: 'Property Condition Before Move-In',
    paragraphs: [
      'Before move-in, SettleSpace will make reasonable efforts to ensure that the listed appliances, fittings and other agreed items are present and in working condition.',
      'Owners are encouraged to provide practical basic fittings such as:',
      {
        type: 'bullets',
        items: [
          'Fans',
          'Lights',
          'Exhaust fans',
          'Curtain rods',
          'Mirrors',
          'Towel hangers',
        ],
      },
      'Additional fittings such as invisible grills or mosquito nets may be provided if agreed between the owner and tenant. Such additions may involve an additional cost or adjustment to the rent.',
      'If repairs, fixes or other agreed work remain pending after the tenant moves in, SettleSpace will make reasonable efforts to ensure that the agreed work is completed as soon as possible.',
      'After the relevant pending issues have been addressed or after a reasonable period has passed, ordinary maintenance and newly arising issues become matters between the owner and tenant unless SettleSpace has separately agreed to assist.',
    ],
  },
  {
    title: 'Post-Move-In Assistance',
    paragraphs: [
      'SettleSpace\'s work does not necessarily end when the tenant moves in.',
      'If repairs, fixes or other agreed work remain pending after move-in, SettleSpace will make reasonable efforts to coordinate their completion as soon as possible.',
      'We also want to make sure that tenants are well settled, comfortable and supported, and are not facing difficulties arising from matters that were part of the agreed rental process.',
      'SettleSpace does not provide indefinite property maintenance or become responsible for every issue that may arise during a tenancy.',
      'Unexpected issues discovered after move-in should generally be communicated by the tenant to the owner directly. SettleSpace may assist with coordination where appropriate, but the owner and tenant remain responsible for resolving matters that fall within their rental agreement.',
    ],
  },
  {
    title: 'Utilities and Maintenance',
    paragraphs: [
      'Regular property maintenance is generally included in the rent unless otherwise agreed between the owner and tenant.',
      'Electricity, gas and other utility bills are generally paid directly to the relevant provider.',
      'Where the arrangement requires payment to the owner, the tenant may make the payment to the owner, who will then make the relevant payment.',
      'The exact responsibility for repairs, maintenance and utilities remains subject to the rental agreement and applicable law.',
    ],
  },
  {
    title: 'Shifting',
    paragraphs: [
      'Shifting is coordinated according to the agreed move-in date and the requirements of the relevant society.',
      'Apartment societies may have designated shifting hours. Extensions may be requested where necessary, subject to the society\'s approval.',
      'Standalone houses may not have the same shifting restrictions.',
      'Where applicable, shifting charges are paid by the tenant directly to the society and are not paid to SettleSpace or the owner.',
      'Move-in will not be facilitated until the applicable SettleSpace service fee has been cleared.',
    ],
  },
  {
    title: 'Notice Period and Lock-In Period',
    paragraphs: [
      'The notice period is generally 45 days from either the owner or tenant before vacating, unless the rental agreement provides otherwise.',
      'The notice period may be reduced to one month by mutual agreement between the owner and tenant.',
      'Where a lock-in period has been agreed in the rental agreement, the parties are expected to comply with it.',
      'SettleSpace may provide reasonable assistance if a lock-in-related issue arises, but SettleSpace does not override the rights or obligations created by the rental agreement.',
      'If a tenant breaches an applicable lock-in obligation, the owner may take lawful action available under the rental agreement and applicable law.',
    ],
  },
  {
    title: 'Rent Payment Delays or Non-Payment',
    paragraphs: [
      'If a tenant delays or stops paying rent, SettleSpace may communicate with the tenant and request that rent be paid on time in accordance with the rental agreement.',
      'If rent remains unpaid, the owner may take appropriate legal or contractual action against the tenant.',
      'SettleSpace does not become responsible for recovering rent from the tenant.',
      'Where the circumstances qualify under the Six-Month Priority Re-Rental Assistance described below, SettleSpace may assist the owner in finding a new tenant while the owner deals with the existing tenancy.',
    ],
  },
  {
    title: 'Owner Breach',
    paragraphs: [
      'If the owner fails to carry out repairs or another obligation that was expressly agreed upon, SettleSpace may first communicate with the owner and request that the matter be addressed.',
      'Where an important agreed condition of the rental agreement is breached, the tenant and owner should act in accordance with the rental agreement and applicable law.',
      'SettleSpace does not instruct either party to take unlawful action.',
      'Where the law and rental agreement permit a tenant to take a particular remedy, the tenant may pursue that remedy independently.',
      'If the rental agreement becomes void or the tenancy otherwise comes to an end because of an owner\'s material breach, SettleSpace may assist the tenant in finding an alternative property under the Six-Month Priority Re-Rental Assistance described below.',
    ],
  },
  {
    title: 'Six-Month Priority Re-Rental Assistance',
    paragraphs: [
      'SettleSpace provides a priority re-rental assistance benefit for qualifying situations arising during the first six months from the date of the rental agreement.',
      'The six-month period begins on the date of the rental agreement and does not restart from the date a tenancy ends.',
      'Where a tenant materially breaches the rental agreement, including through serious non-payment, repeated violations of agreed conditions or conduct resulting in material complaints or other circumstances that cause the tenancy to become void or end in accordance with the agreement and applicable law, SettleSpace may provide the owner with priority assistance in finding a replacement tenant.',
      'Where an owner materially breaches the rental agreement and the tenancy becomes void or ends as a result, SettleSpace may provide the tenant with priority assistance in finding an alternative rental property.',
      'Where an owner decides to sell the property during the tenancy and the tenancy consequently needs to end or the tenant needs to relocate, SettleSpace may also assist the tenant in finding an alternative property where the situation falls within the applicable six-month period.',
      'SettleSpace will make reasonable efforts to assist the affected party as soon as reasonably possible.',
      'However, SettleSpace does not guarantee that a suitable tenant or property will be found within six months or at all.',
      'This benefit is an assistance service only. It is not:',
      {
        type: 'bullets',
        items: [
          'A financial guarantee',
          'Insurance',
          'An indemnity',
          'Compensation for loss',
          'A guarantee of a replacement tenant or property',
          'A guarantee that a tenancy will continue',
        ],
      },
      'No additional SettleSpace service fee will be charged for this priority re-rental assistance.',
    ],
  },
  {
    title: 'Sale of Property During Tenancy',
    paragraphs: [
      'If the owner decides to sell the property during the tenancy, the tenant must be informed of the proposed sale.',
      'If the tenancy is affected as a result of the sale, SettleSpace may assist the tenant in finding another suitable rental property. Where this occurs within the six-month period covered by SettleSpace\'s Priority Re-Rental Assistance, such assistance will be provided without any additional SettleSpace service fee, subject to the terms and limitations of that assistance.',
      'SettleSpace does not guarantee that a replacement property will be found within any particular period.',
    ],
  },
  {
    title: 'Lock-in Period',
    paragraphs: [
      'Where the rental agreement contains a lock-in period, the owner and tenant are expected to comply with the terms of that agreement.',
      'SettleSpace does not have the authority to terminate or alter the lock-in period. Any breach of the lock-in period may be dealt with by the affected party in accordance with the rental agreement and applicable law.',
      'If the tenancy becomes void or is otherwise terminated as a result of a breach by the tenant or owner during the applicable six-month period from the agreement date, the relevant party may receive Priority Re-Rental Assistance as described in these Terms.',
    ],
  },
  {
    title: 'Notice Period',
    paragraphs: [
      'Unless otherwise agreed in the rental agreement, the standard notice period for vacating the property is 45 days.',
      'The notice period may be reduced to one month where the owner and tenant mutually agree to the reduced period.',
      'Any notice, termination or request to vacate remains subject to the terms of the rental agreement and applicable law.',
    ],
  },
  {
    title: 'Rent Payment and Delays',
    paragraphs: [
      'The tenant is responsible for paying rent to the owner in accordance with the rental agreement.',
      'If rent payments are delayed, SettleSpace may communicate with the tenant and request that the outstanding rent be paid promptly to avoid inconvenience or further delays for the owner.',
      'If the tenant completely stops paying rent, the owner may take appropriate action against the tenant in accordance with the rental agreement and applicable law.',
      'SettleSpace may, where applicable, assist the owner in finding a new tenant while the existing rental matter is being dealt with.',
      'SettleSpace does not guarantee or assume responsibility for the payment of rent by a tenant.',
    ],
  },
  {
    title: 'Repairs and Owner Obligations',
    paragraphs: [
      'Where repairs, fixes or other work have been agreed upon as the owner\'s responsibility, SettleSpace may coordinate with the owner to have the matter addressed.',
      'If the owner fails to carry out an important obligation or breaches a material condition of the rental agreement, the tenant should communicate the matter to the owner and may take any action available to them under the rental agreement and applicable law.',
      'SettleSpace may assist with communication and coordination but does not have authority to independently alter the rental agreement or determine the legal rights of either party.',
    ],
  },
  {
    title: 'Property Condition and Undisclosed Issues',
    paragraphs: [
      'SettleSpace makes reasonable efforts to ensure that relevant property information provided by the owner is communicated accurately to the tenant.',
      'Where an issue with the property is discovered after the tenancy or move-in has commenced and the issue was not known to SettleSpace, the tenant should promptly inform the owner.',
      'The owner and tenant should then communicate directly to determine the appropriate solution. SettleSpace may assist with coordination where reasonably required, but is not responsible for issues that were unknown, undisclosed or outside its reasonable control.',
    ],
  },
  {
    title: 'Property Inspection at the End of Tenancy',
    paragraphs: [
      'SettleSpace may conduct an inspection of the property when the tenant is vacating or has been asked to vacate, generally during the final days of the tenancy.',
      'The inspection may identify damages, breakages, leaks, missing items or other conditions that appear to go beyond normal use or are inconsistent with the rental agreement.',
      'SettleSpace may document the condition of the property through photographs or video and share the relevant findings with the owner.',
      'The owner and tenant remain responsible for discussing and resolving any dispute regarding damage, repairs, deductions or other amounts in accordance with the rental agreement and applicable law.',
    ],
  },
  {
    title: 'Security Deposit',
    paragraphs: [
      'The security deposit is paid directly to the owner. SettleSpace does not hold the first month\'s rent, security deposit or booking amount on behalf of the owner or tenant.',
      'The deposit is generally settled on the day the tenant vacates, after the property has been inspected and the handover process has been completed.',
      'Where damages, leaks, breakages or other issues are identified, the tenant and owner are responsible for resolving the matter between themselves in accordance with the rental agreement.',
      'Where reasonably possible, the tenant may be given one to three days to address identified issues before the final handover, depending on the timing of the inspection and the circumstances.',
      'SettleSpace\'s role is limited to conducting or coordinating the inspection and reporting relevant findings to the owner.',
    ],
  },
  {
    title: 'Responsibility for Property Damage',
    paragraphs: [
      'The tenant is responsible for damage to the property or its contents caused by the tenant, their occupants, guests or other persons for whom the tenant is responsible, except where such damage arises from normal wear and tear or circumstances otherwise covered by the rental agreement.',
      'Any deductions from the security deposit should be handled in accordance with the rental agreement and applicable law.',
      'SettleSpace does not independently determine the amount of any deduction.',
    ],
  },
  {
    title: 'Move-in Condition and Pending Repairs',
    paragraphs: [
      'Before the tenant moves into the property, SettleSpace makes reasonable efforts to ensure that listed appliances, fittings and other agreed items are present and in working condition.',
      'Where repairs or other agreed work remain pending after the tenant has moved in, SettleSpace will coordinate with the relevant party and make reasonable efforts to have the work completed as soon as possible.',
      'SettleSpace\'s assistance relates to issues that were identified, agreed upon or pending as part of the rental process. Ordinary maintenance or issues arising after the tenancy has progressed are generally matters between the owner and tenant unless otherwise agreed.',
    ],
  },
  {
    title: 'Unexpected Discoveries After Move-in',
    paragraphs: [
      'If the tenant discovers an unexpected issue with the property after moving in, the tenant should communicate the issue directly to the owner.',
      'SettleSpace may assist with communication or coordination where appropriate, but the owner and tenant remain responsible for resolving the matter between themselves unless the issue relates to work that SettleSpace had specifically agreed to coordinate.',
    ],
  },
  {
    title: 'Police Verification',
    paragraphs: [
      'Police verification is conducted to verify the relevant personal details and other information required for the rental process.',
      'If police verification cannot be completed or identifies an issue relating to the tenant or owner, the relevant party will be informed and may be required to rectify the issue before the rental process can continue.',
      'Where the issue prevents the tenancy from proceeding, SettleSpace may halt the process to avoid future legal or procedural problems and may assist the owner in finding another tenant where applicable.',
      'SettleSpace does not guarantee the outcome or processing time of police verification.',
    ],
  },
  {
    title: 'Accuracy and Verification of Documents',
    paragraphs: [
      'Owners and tenants are responsible for providing accurate, current and genuine documents and information.',
      'SettleSpace makes reasonable efforts to cross-check documents provided to it and verify relevant information required for the rental process.',
      'Where incorrect or incomplete information is identified, the relevant party will be given an opportunity to provide corrected or updated documents.',
      'If the required information cannot be properly verified, SettleSpace may halt or discontinue the transaction to avoid potential legal, procedural or documentation-related problems.',
      'SettleSpace is not responsible for consequences arising from false, misleading, forged or inaccurate information provided by an owner or tenant where such information could not reasonably have been identified through the verification process.',
    ],
  },
  {
    title: 'Society NOC and Society Procedures',
    paragraphs: [
      'Where a Society NOC or other society approval is required, SettleSpace will coordinate with the owner, tenant and relevant society representatives to complete the required process.',
      'The owner and tenant are responsible for complying with the applicable society rules and requirements.',
      'If an issue arises with the society process, SettleSpace will assist the relevant parties in communicating and working towards a reasonable solution.',
      'SettleSpace does not control the decisions, requirements, processing times or internal procedures of any society or managing authority.',
    ],
  },
  {
    title: 'Third-party Service Providers',
    paragraphs: [
      'SettleSpace may engage or coordinate with third-party companies, individuals or service providers for services such as rental agreement preparation or registration, biometric appointments, police verification, cleaning, pest control and other related processes.',
      'Where a third party is involved, SettleSpace may connect the relevant party directly with that provider where this is reasonably required for clarity, communication or completion of the process.',
      'SettleSpace will continue to assist with coordination and communication where appropriate.',
      'However, SettleSpace is not responsible for delays, decisions, errors or failures caused solely by an independent third-party provider or government authority.',
    ],
  },
  {
    title: 'Government and Portal Delays',
    paragraphs: [
      'Certain parts of the rental process depend on government portals, registration systems, police verification systems or other external platforms.',
      'Delays may occur due to government or sub-registrar portal congestion, server issues, technical difficulties, weekends, public holidays, government holidays or other circumstances outside SettleSpace\'s reasonable control.',
      'Such delays do not constitute a breach by SettleSpace.',
      'SettleSpace will nevertheless make reasonable efforts to monitor the process, communicate relevant delays and complete the required work as soon as the relevant external system becomes available.',
    ],
  },
  {
    title: 'Direct Dealing Between Owner and Tenant',
    paragraphs: [
      'Once SettleSpace introduces an owner and tenant to each other for the purpose of a rental transaction, the parties are expected to allow SettleSpace to coordinate the rental process.',
      'The owner and tenant should not independently negotiate, complete or bypass SettleSpace\'s involvement in the rental process where SettleSpace is engaged to provide its rental services.',
      'If the owner and tenant choose to deal directly with each other in a manner that bypasses SettleSpace or materially interferes with its role in the rental process, SettleSpace may withdraw its services and discontinue its involvement in the transaction.',
    ],
  },
  {
    title: 'Sharing of Contact Details',
    paragraphs: [
      'Owner and tenant contact details are generally not intended to be exchanged directly during the initial rental process unless required for legitimate coordination.',
      'Where the owner and tenant independently exchange contact details and begin dealing directly with one another, SettleSpace may withdraw from the rental process where such direct dealing affects or bypasses its role.',
      'The owner and tenant will be informed that SettleSpace\'s continued involvement depends upon the rental process being coordinated through SettleSpace.',
    ],
  },
  {
    title: 'Existing Relationship Between Owner and Tenant',
    paragraphs: [
      'If an owner and tenant already know one another or have an existing relationship, they may choose to deal with each other independently.',
      'Alternatively, they may choose to engage SettleSpace for its rental services and the associated benefits.',
      'Where SettleSpace is engaged, its normal service process and applicable service fee will apply.',
    ],
  },
  {
    title: 'SettleSpace Service Fee',
    paragraphs: [
      'For rental services, the SettleSpace service fee is one month\'s rent for rental tenures of one, two or three years.',
      'The applicable service fee is payable only after the required documentation process has been completed, including submission and receipt of the rental agreement, completion of police verification and completion of the Society NOC where applicable.',
      'The SettleSpace service fee must be cleared before the tenant\'s move-in is completed.',
      'The service fee does not include government charges, agreement charges, police verification charges or shifting charges where applicable, unless expressly stated otherwise.',
    ],
  },
  {
    title: 'Payment Details and Transfer Errors',
    paragraphs: [
      'SettleSpace will provide the relevant payment or account details accurately to the concerned party where SettleSpace is responsible for communicating those details.',
      'SettleSpace is not responsible for an incorrect transfer made by a tenant or owner where the correct payment details were provided by SettleSpace and the error occurred due to the payer entering or using incorrect information.',
      'Any payment made directly between the owner and tenant remains the responsibility of those parties.',
    ],
  },
  {
    title: 'Cash, UPI and Other Payments',
    paragraphs: [
      'Payments between an owner and tenant may be made by cash, UPI, bank transfer or another mutually accepted method, subject to the owner\'s and tenant\'s agreement and applicable law.',
      'SettleSpace generally recommends UPI or bank transfers, particularly for substantial amounts such as rent and security deposits, because these methods provide clearer transaction records.',
      'SettleSpace does not hold the first month\'s rent, security deposit or booking amount.',
    ],
  },
  {
    title: 'Document Authenticity',
    paragraphs: [
      'Documents provided to SettleSpace may be reviewed and cross-checked for legitimacy, relevance and consistency with the information required for the rental process.',
      'Where a document appears incorrect, inconsistent, outdated or otherwise unsuitable, SettleSpace may request a corrected or updated document.',
      'SettleSpace does not guarantee the authenticity of a document where a fraudulent or misleading document cannot reasonably be detected through the verification process.',
    ],
  },
  {
    title: 'Intellectual Property',
    paragraphs: [
      'The SettleSpace name, logo, branding, website content, design elements, written materials and other original intellectual property belonging to SettleSpace may not be copied, reproduced, modified, distributed or commercially used without prior written permission.',
      'Unauthorised use of SettleSpace\'s branding or intellectual property may result in appropriate action under applicable law.',
    ],
  },
  {
    title: 'Governing Law and Jurisdiction',
    paragraphs: [
      'These Terms of Service are governed by the laws of India.',
      'Subject to applicable law and the dispute-resolution provisions below, the courts having appropriate jurisdiction in Navi Mumbai, Maharashtra shall have jurisdiction over disputes arising from or relating to these Terms or the services provided by SettleSpace.',
      'Nothing in these Terms prevents either party from approaching any authority or court that has jurisdiction under applicable law.',
    ],
  },
  {
    title: 'Dispute Resolution',
    paragraphs: [
      'Where a dispute arises between SettleSpace and an owner or tenant in relation to the services provided by SettleSpace, the concerned party should first contact SettleSpace and provide an opportunity to address and resolve the matter amicably.',
      'Where SettleSpace\'s involvement is relevant to a dispute between an owner and tenant, SettleSpace may assist the parties in communicating and attempting to resolve the matter amicably.',
      'Nothing in these Terms prevents a party from taking legal action or approaching an appropriate authority where such action is permitted or required by law.',
    ],
  },
  {
    title: 'Acceptance of These Terms',
    paragraphs: [
      'These Terms of Service are made available on the SettleSpace website and apply to persons who use or engage SettleSpace\'s rental services.',
      'By engaging SettleSpace for its rental services, the owner or tenant acknowledges that they have had the opportunity to review these Terms and agrees to be bound by the terms applicable to the services being provided.',
      'Where appropriate, SettleSpace may obtain additional confirmation or acceptance of these Terms electronically through email, WhatsApp, an online form or another electronic communication.',
      'The Terms should be read together with any specific rental agreement, service communication or other written arrangement applicable to the transaction. In the event of a conflict, the applicable rental agreement will govern matters specifically relating to the tenancy, while these Terms govern the relationship between SettleSpace and the relevant customer to the extent applicable.',
    ],
  },
  {
    title: 'Contact Information',
    paragraphs: [
      'SettleSpace operates as a sole proprietorship under the proprietorship of Hitesh Parmar.',
      'For questions regarding these Terms of Service, the rental services provided by SettleSpace, or any related concern, you may contact:',
      {
        type: 'contact',
        lines: [
          'SettleSpace',
          'Proprietor: Hitesh Parmar',
          'Email: hitesh@settlespace.in',
          'Phone: +91 8591019785',
          'Website: settlespace.in',
        ],
      },
      'Service area: Navi Mumbai, Maharashtra, India, primarily Kharghar, with services also available in areas such as CBD Belapur, Seawoods and Nerul.',
      'SettleSpace may update these Terms of Service from time to time to reflect changes to its services, business practices or applicable legal requirements. The latest version published on the SettleSpace website will apply to future use of the services.',
    ],
  },
];

export default function TermsOfServicePage() {
  const navigate = useNavigate();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://settlespace.in';

  const goBack = () => {
    navigate('/');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.getElementById('site-footer')?.scrollIntoView({ behavior: 'auto' });
      });
    });
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Terms of Service',
    url: `${siteUrl}/terms-of-service`,
    description:
      'The terms governing rental assistance and coordination services provided by SettleSpace in Navi Mumbai.',
    inLanguage: 'en',
    isPartOf: {
      '@type': 'WebSite',
      name: 'SettleSpace',
      url: siteUrl,
    },
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <JsonLd data={jsonLd} />
      <div className="w-full px-6 md:px-12 lg:px-16 py-28 md:py-32">
        <div className="max-w-3xl mx-auto">
          <div className="bg-background-100 rounded-[48px] px-8 md:px-14 py-10 md:py-14">
          {/* Back to Home link */}
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-950 transition-colors mb-10 cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            <span>Back to Home</span>
          </button>

          {/* Main Heading */}
          <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-4 tracking-tight">
            Terms of&nbsp;&nbsp;Service
          </h1>

          {/* Effective Date */}
          <p className="text-sm text-foreground-500 mb-12 md:mb-16">
            Effective date: 26 August 2026
          </p>

          {/* Intro */}
          <p className="text-sm md:text-base text-foreground-800 leading-relaxed mb-5">
            These Terms of Service (&quot;Terms&quot;) govern the use of the rental-related services provided by SettleSpace (&quot;SettleSpace&quot;, &quot;we&quot;, &quot;us&quot; or &quot;our&quot;).
          </p>
          <p className="text-sm md:text-base text-foreground-800 leading-relaxed mb-5">
            SettleSpace is operated as a sole proprietorship by Hitesh Parmar and currently provides rental assistance and related coordination services in Navi Mumbai, Maharashtra, including areas such as Kharghar, CBD Belapur, Seawoods, Nerul and other locations where SettleSpace agrees to provide its services.
          </p>
          <p className="text-sm md:text-base text-foreground-800 leading-relaxed mb-14">
            By using SettleSpace&apos;s website, contacting us, requesting our services, or proceeding with a rental transaction through SettleSpace, you acknowledge that you have read and understood these Terms and agree to be bound by them.
          </p>

          {/* Sections */}
          <div className="flex flex-col gap-10 md:gap-12">
            {sections.map((section, idx) => (
              <section key={idx}>
                <h2 className="font-googleSans font-bold text-lg md:text-xl text-foreground-950 mb-4">
                  {idx + 1}. {section.title}
                </h2>
                <div className="flex flex-col gap-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  {section.paragraphs.map((para, idx) => {
                    if (typeof para === 'string') {
                      return <p key={idx}>{para}</p>;
                    }
                    if (para.type === 'bullets') {
                      return (
                        <ul key={idx} className="flex flex-col gap-2 pl-5">
                          {para.items.map((item, i) => (
                            <li key={i} className="relative pl-4 before:content-['•'] before:absolute before:left-0 before:text-foreground-400">
                              {item}
                            </li>
                          ))}
                        </ul>
                      );
                    }
                    if (para.type === 'contact') {
                      return (
                        <div key={idx} className="flex flex-col gap-1 bg-background-50 rounded-lg px-5 py-4">
                          {para.lines.map((line, i) => (
                            <p key={i}>{line}</p>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </section>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="mt-14 md:mt-16 pt-10 border-t border-background-200/60">
            <button
              type="button"
              onClick={goBack}
              className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-950 text-background-50 rounded-md text-sm font-medium hover:scale-105 hover:bg-foreground-800 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Back to SettleSpace
            </button>
          </div>
          </div>
        </div>
      </div>
    </>
  );
}